/**
 * Kova API client. Every shape here mirrors backend/main.py `public()` output
 * and the report-card / live-grade endpoints.
 */

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8080';
export const WS_URL = (import.meta.env.VITE_WS_URL as string | undefined) ?? 'ws://localhost:8080';

export interface TeamSide {
  id: string;
  name: string;
  abbreviation: string;
  logo?: string;
  score?: string | number | null;
  record?: string | null;
  rank?: number | null;
  color?: string | null;
  alt_color?: string | null;
}

export interface Leader { team: string | null; category: string; player: string | null; position: string | null; headshot: string | null; line: string | null }

export interface GameWeather {
  stadium?: string;
  roof?: 'outdoors' | 'dome' | 'retractable' | string;
  indoors?: boolean;
  forecast_available?: boolean;
  temperature_f?: number | null;
  wind_mph?: number | null;
  gust_mph?: number | null;
  precipitation_probability?: number | null;
  impact?: { level: string; flags: string[] };
  note?: string | null;
  error?: string;
}
export interface TeamMeta { id: string; name: string; abbreviation: string; logo: string | null; color: string | null; alt_color: string | null }

export type League = 'nfl' | 'cfb';

export interface Game {
  id: string;
  league?: League;
  name: string;
  short_name?: string;
  date: string;
  week?: number;
  status: 'pre' | 'in' | 'post' | string;
  status_detail?: string;
  completed?: boolean;
  home_team: TeamSide;
  away_team: TeamSide;
  venue?: string;
  venue_city?: string | null;
  indoor?: boolean | null;
  broadcast?: string | null;
  odds?: string | null;
}

export interface MarketPlay {
  market: 'moneyline' | 'spread' | 'total';
  side: string;
  line: number | null;
  odds: number;
  model_probability: number;
  model_probability_raw: number;
  market_fair_probability: number;
  implied_probability: number;
  edge_percent: number;
  expected_value_per_100: number;
  positive_ev: boolean;
  kelly: { fractional_kelly: number; suggested_stake_fractional?: number; has_edge: boolean };
}

export interface BettingRecommendation {
  matchup: string;
  recommendation: string;
  market_status: 'open' | 'closed_or_unavailable';
  best_play: MarketPlay | null;
  all_markets: MarketPlay[];
  confidence: 'low' | 'moderate' | 'high' | null;
  key_pattern: string | null;
  contradicting_pattern: string | null;
  expected_margin: { low: number; high: number } | null;
  summary: string | null;
  market: { status: string; note: string | null; prices: { spread: number | null; total: number | null; home_ml: number | null; away_ml: number | null } };
  bankroll: number;
}

export interface ReasoningStep {
  step: number;
  factor: string;
  evidence: string;
  effect: string;
}

export interface Prediction {
  predicted_winner: string;
  confidence: number | null;
  confidence_label: string | null;
  key_factors: string[];
  injury_impact: { impact_level?: string; key_injuries?: string[] };
  historical_precedent: { description?: string; record?: string };
  betting_line_comparison: string;
  expected_margin: { low: number; high: number } | null;
  reasoning: string;
}

export interface Storyline {
  title: string;
  type: string;
  data_point: string;
  why_it_matters: string;
  betting_relevance: 'high' | 'medium' | 'low';
  small_sample: boolean;
}

export interface Causation {
  answer: string;
  primary_cause: { cause: string; evidence: string; impact: string };
  contributing_causes: { cause: string; evidence: string; impact: string }[];
  turning_point: { description: string; quarter: number; clock: string; win_prob_swing: string } | null;
  what_was_not_the_cause: string | null;
  confidence: string;
}

export interface AnalysisResponse {
  intent: 'betting' | 'decision' | 'causation' | 'narrative' | 'prediction';
  home_team?: string;
  away_team?: string;
  sport?: string;
  prediction?: Prediction;
  betting_recommendation?: BettingRecommendation;
  storylines?: Storyline[];
  causation?: Causation;
  reasoning_steps?: ReasoningStep[];
  sources_skipped?: number;
}

export type Grade = 'Optimal' | 'Acceptable' | 'Questionable' | 'Wrong';

export interface DecisionSummary {
  category: string;
  decision: string;
  optimal: string | null;
  grade: Grade;
  wp_impact_pct: number | null;
  epa_cost: number | null;
  quarter: number | null;
  down_distance: string | null;
  yardline_100: number | null;
  score_differential: number | null;
  description: string | null;
}

export interface PhaseCard {
  title: string;
  decisions: number;
  /** Games these decisions span, so a total can be shown per game and compared across coaches. */
  games: number;
  Optimal: number;
  Acceptable: number;
  Questionable: number;
  Wrong: number;
  optimal_rate: number | null;
  net_wp_impact_pct: number;
  wp_impact_per_game_pct: number | null;
  wp_decisions: number;
  net_epa_vs_optimal: number;
  epa_per_game: number | null;
  playcall_decisions: number;
  worst_decision: DecisionSummary | null;
}

export interface TeamReport {
  overall: PhaseCard & { head_coach?: string | null };
  phases: Record<'offense' | 'defense' | 'special_teams' | 'head_coach', PhaseCard>;
  worst_decisions: DecisionSummary[];
  all_decisions: DecisionSummary[];
}

export interface LiveAlert {
  team: string;
  quarter: number | null;
  clock: string | null;
  category: string;
  decision: string;
  optimal: string | null;
  grade: Grade;
  wp_cost_pct: number;
  description: string;
}

export interface ReportCard {
  game_id: string;
  gradeable_decisions: number;
  teams: Record<string, TeamReport>;
  live_alerts?: LiveAlert[];
  coverage?: string;
  error?: string;
}

export interface SeasonReport {
  coach: string | null;
  team: string | null;
  team_logo?: string | null;
  team_color?: string | null;
  team_name?: string | null;
  games: number;
  league: League;
  season_year?: number | null;
  seasons?: number[];
  season: PhaseCard;
  phases: Record<'offense' | 'defense' | 'special_teams' | 'head_coach', PhaseCard>;
  per_game: Record<string, PhaseCard & { week?: number | null; season?: number | null; opponent?: string | null; postseason?: boolean }>;
  worst_decisions: DecisionSummary[];
}

export interface LeaderboardRow {
  coach: string;
  team: string;
  team_logo?: string | null;
  team_color?: string | null;
  team_name?: string | null;
  games: number;
  decisions: number;
  optimal_rate: number | null;
  net_wp_impact_pct: number;
  wp_impact_per_game_pct: number | null;
  net_epa_vs_optimal: number;
  epa_per_game: number | null;
  wrong: number;
  questionable: number;
}


export interface TrendSeason {
  season: number;
  games: number;
  decisions: number;
  optimal_rate: number | null;
  wp_impact_per_game_pct: number | null;
  wrong: number;
  questionable: number;
  /** A season still being played: shown, but it does not set the direction. */
  partial: boolean;
}

export interface CoachTrend {
  coach: string;
  phase: string;
  league: League;
  seasons: TrendSeason[];
  change_per_game_pct: number | null;
  direction: 'improving' | 'declining' | 'steady' | null;
  /** Which seasons the direction was drawn from, so a partial year is visibly excluded. */
  direction_from: number[] | null;
  team_logo?: string | null;
  team_color?: string | null;
  team_name?: string | null;
}

export interface CostlyGame {
  game_id: string;
  coach: string;
  team: string | null;
  opponent: string | null;
  season: number | null;
  week: number | null;
  decisions: number;
  wp_cost_pct: number;
  wrong: number;
  questionable: number;
  worst_decision: DecisionSummary | null;
  team_logo?: string | null;
  team_color?: string | null;
  team_name?: string | null;
}

export interface CoachSide {
  games: number;
  decisions: number;
  optimal_rate: number | null;
  wp_impact_per_game_pct: number | null;
  wrong: number;
  questionable: number;
}

export interface CoachComparison {
  phase: string;
  league: League;
  season: number | null;
  coaches: Record<string, CoachSide>;
  gap_per_game_pct?: number;
  better?: string | null;
}

export interface TendencyOption {
  chose: number;
  chose_rate: number;
  was_optimal: number;
  optimal_rate: number;
  /** Positive means he reaches for it more often than it is the right answer. */
  bias: number;
}

export interface Tendencies {
  coach: string;
  category: string;
  league: League;
  season: number | null;
  decisions: number;
  options: Record<string, TendencyOption>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { headers: { 'Content-Type': 'application/json' }, ...init });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      detail = (await res.json()).detail ?? detail;
    } catch {
      // keep statusText
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

export const api = {
  games: (league: League = 'nfl') => request<Game[]>(`/upcoming-games?league=${league}`),
  recentGames: (league: League = 'nfl') => request<Game[]>(`/recent-games?league=${league}`),
  pastGames: (league: League, season: number, week: number) =>
    request<{ league: League; season: number; week: number; games: Game[] }>(
      `/past-games?league=${league}&season=${season}&week=${week}`),
  analyze: (query: string, extra: Record<string, unknown> = {}) =>
    request<AnalysisResponse>('/analyze', { method: 'POST', body: JSON.stringify({ query, ...extra }) }),
  bettingCoach: (g: Game, bankroll = 1000) =>
    request<AnalysisResponse>('/betting-coach', {
      method: 'POST',
      body: JSON.stringify({
        query: `Best bet for ${g.away_team.name} at ${g.home_team.name}`, home_team: g.home_team.name, away_team: g.away_team.name,
        sport: g.league ?? 'nfl', bankroll, venue: { city: g.venue_city ?? null, indoor: g.indoor ?? null },
      }),
    }),
  narratives: (home: string, away: string, sport: League = 'nfl') =>
    request<AnalysisResponse>('/narratives', { method: 'POST', body: JSON.stringify({ home_team: home, away_team: away, sport }) }),
  why: (gameId: string, query: string, sport: League = 'nfl') => request<AnalysisResponse>('/why', { method: 'POST', body: JSON.stringify({ game_id: gameId, query, sport }) }),
  liveGrade: (espnId: string, league: League = 'nfl') => request<ReportCard>(`/live-grade/${espnId}?league=${league}`),
  reportCard: (gameId: string, league: League = 'nfl') => request<ReportCard>(`/report-card/${gameId}?league=${league}`),
  coachSeason: (coach: string, league: League = 'nfl', season?: number) =>
    request<SeasonReport>(`/coach/${encodeURIComponent(coach)}/season?league=${league}${season ? `&season=${season}` : ''}`),
  leaderboard: (season?: number, phase = 'head_coach', league: League = 'nfl') =>
    request<{ season: number | null; seasons: number[]; coaches: LeaderboardRow[] }>(`/leaderboard?phase=${phase}&league=${league}${season ? `&season=${season}` : ''}`),
  gameLeaders: (espnId: string, league: League = 'nfl') => request<{ leaders: Leader[] }>(`/game-leaders/${espnId}?league=${league}`),
  gameWeather: (espnId: string, league: League = 'nfl') => request<GameWeather>(`/game-weather/${espnId}?league=${league}`),
  teamMeta: (league: League = 'nfl') => request<Record<string, TeamMeta>>(`/team-meta?league=${league}`),
  coachTrend: (coach: string, league: League = 'nfl', seasons = 5) =>
    request<CoachTrend>(`/coach/${encodeURIComponent(coach)}/trend?league=${league}&seasons=${seasons}`),
  coachTendencies: (coach: string, league: League = 'nfl', category = 'fourth_down') =>
    request<Tendencies>(`/coach/${encodeURIComponent(coach)}/tendencies?league=${league}&category=${category}`),
  costliestGames: (league: League = 'nfl', season?: number, limit = 20) =>
    request<{ league: League; season: number | null; seasons: number[]; games: CostlyGame[] }>(
      `/costliest-games?league=${league}&limit=${limit}${season ? `&season=${season}` : ''}`),
  coachCompare: (first: string, second: string, league: League = 'nfl', season?: number) =>
    request<CoachComparison>(
      `/coach-compare?first=${encodeURIComponent(first)}&second=${encodeURIComponent(second)}&league=${league}${season ? `&season=${season}` : ''}`),
  health: () => request<{ status: string; model: string }>('/health'),
};

export const isLive = (g: Game) => g.status === 'in';
export const isFinal = (g: Game) => g.status === 'post' || g.completed === true;

export function fmtOdds(n: number | null | undefined): string {
  if (n === null || n === undefined) return '';
  return n > 0 ? `+${n}` : `${n}`;
}

export function fmtSigned(n: number | null | undefined, digits = 1, suffix = ''): string {
  if (n === null || n === undefined) return '';
  const v = n.toFixed(digits);
  return `${n > 0 ? '+' : ''}${v}${suffix}`;
}

export function kickoff(iso: string): { day: string; time: string } {
  const d = new Date(iso);
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase(),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
}
